import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaComment, FaUser, FaEnvelope, FaPhone, FaTrash, FaCheck, FaSpinner, FaClock } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import './ChatManager.css';

const ChatManager = () => {
  const { t, language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/chat/messages`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      showMessage('error', t('admin.messageLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    // Обновляем сообщения каждые 15 секунд и уведомляем о новых
    const interval = setInterval(() => {
      fetchMessages();
      // Уведомляем родительский компонент об обновлении счетчиков
      window.dispatchEvent(new Event('admin:updateCounts'));
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      setLoading(true);
      await axios.put(`${API_BASE_URL}/api/chat/messages/${id}/read`);
      showMessage('success', t('admin.messageMarkedRead'));
      fetchMessages();
      // Уведомляем родительский компонент об обновлении счетчиков
      window.dispatchEvent(new Event('admin:updateCounts'));
    } catch (error) {
      console.error('Error marking message as read:', error);
      showMessage('error', t('admin.messageStatusUpdateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.deleteMessageConfirm'))) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/api/chat/messages/${id}`);
      showMessage('success', t('admin.messageDeleted'));
      fetchMessages();
      // Уведомляем родительский компонент об обновлении счетчиков
      window.dispatchEvent(new Event('admin:updateCounts'));
    } catch (error) {
      console.error('Error deleting message:', error);
      showMessage('error', t('admin.messageDeleteError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    const localeCode = language === 'en' ? 'en-US' : 'ru-RU';
    return new Date(dateStr).toLocaleString(localeCode, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <motion.div
      className="chat-manager"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="chat-manager-header">
        <h2>
          <FaComment /> {t('admin.messages')}
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount}</span>
          )}
        </h2>
        <button onClick={fetchMessages} disabled={loading} className="refresh-btn">
          {loading ? <FaSpinner className="spinner" /> : t('admin.refresh')}
        </button>
      </div>

      {message.text && (
        <motion.div
          className={`alert ${message.type}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          {message.text}
        </motion.div>
      )}

      {loading && messages.length === 0 && (
        <div className="loading-container">
          <FaSpinner className="spinner" />
          <span>{t('admin.loadingMessages')}</span>
        </div>
      )}

      {!loading && messages.length === 0 && (
        <div className="no-messages">
          <FaComment />
          <p>{t('admin.noMessages')}</p>
        </div>
      )}

      {messages.length > 0 && (
        <div className="messages-list">
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              className={`message-item ${!msg.read ? 'unread' : ''}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="message-header">
                <div className="message-meta">
                  <div className="message-time">
                    <FaClock />
                    <span>{formatDateTime(msg.createdAt)}</span>
                  </div>
                  {!msg.read && <span className="unread-indicator">Новое</span>}
                </div>
                <div className="message-actions">
                  {!msg.read && (
                    <button
                      onClick={() => handleMarkAsRead(msg.id)}
                      disabled={loading}
                      className="action-btn read-btn"
                      title={t('admin.markAsRead')}
                    >
                      <FaCheck />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(msg.id)}
                    disabled={loading}
                    className="action-btn delete-btn"
                    title={t('admin.delete')}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="message-content">
                {(msg.name || msg.email || msg.phone) && (
                  <div className="message-contact-info">
                    {msg.name && (
                      <div className="contact-item">
                        <FaUser />
                        <span>{msg.name}</span>
                      </div>
                    )}
                    {msg.email && (
                      <div className="contact-item">
                        <FaEnvelope />
                        <a href={`mailto:${msg.email}`}>{msg.email}</a>
                      </div>
                    )}
                    {msg.phone && (
                      <div className="contact-item">
                        <FaPhone />
                        <a href={`tel:${msg.phone}`}>{msg.phone}</a>
                      </div>
                    )}
                  </div>
                )}

                <div className="message-text">
                  <p>{msg.message}</p>
                </div>

                {msg.notifications && (
                  <div className="message-notifications">
                    <span className="notification-label">{t('admin.notifications')}:</span>
                    {msg.notifications.telegram && (
                      <span className="notification-badge telegram">Telegram</span>
                    )}
                    {msg.notifications.whatsapp && (
                      <span className="notification-badge whatsapp">WhatsApp</span>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default ChatManager;

